import _ from "lodash";
import { useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import * as yup from "yup";
import { Text } from "@netapp/bxp-design-system-react";
import { apiClient } from "../../utils/clients";
import { ReactComponent as OrgIcon } from "./ic_company.svg";
import { ReactComponent as FolderIcon } from "./ic_folder.svg";
import { ReactComponent as ProjectIcon } from "./icon-project.svg";
import styles from "./components/Settings.module.scss";

export const emailRegex = /^(?=.{0,255}$)[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.([a-zA-Z]{2,20})$/;
export const GRID_PAGESIZE = 10;

export const getPaginationQuery = (
    pageSize,
    sortState,
    filterState,
    pageIndex,
    defaultFilters = null
) => {
    const query = {
        limit: pageSize,
        skip: pageSize * pageIndex,
    };
    if (sortState && sortState.column && sortState.sortOrder) {
        query.order_by = `${sortState.column}${sortState.sortOrder === ' desc' ? sortState.sortOrder : ''}`;
    }
    if (defaultFilters) {
        query.filter = defaultFilters;
    }
    return query;
};

export function useAPIQuery(urlObject) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(urlObject?.url ? true: false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!_.isEmpty(urlObject?.url)) {
            (async () => {
                setIsLoading(true);
                setError(null);
                try {
                    let response;
                    if(urlObject.query) {
                        response = await apiClient.get(urlObject.url, { params: urlObject?.query });
                    } else {
                        response = await apiClient.get(urlObject.url);
                    }
                    const data = JSON.parse(response.data);
                    setData(data);
                }
                catch (err) {
                    setError(err);
                } finally {
                    setIsLoading(false);
                }
            })();
        }
    }, [urlObject]);
    return { data, isLoading, error }
}

export const getFoldersAndProjectsMap = (resources) => {
    const foldersAndProjectsMap = {
        folders: [],
        projects: []
    };
    _.forEach(resources, function (resource) {

        if (!_.isEmpty(resource["childScopes"])) {
            const childScopesFoldersAndProjectsMap = getFoldersAndProjectsMap(resource["childScopes"]);
            foldersAndProjectsMap.folders = [...foldersAndProjectsMap.folders, ...childScopesFoldersAndProjectsMap.folders];
            foldersAndProjectsMap.projects = [...foldersAndProjectsMap.projects, ...childScopesFoldersAndProjectsMap.projects];
        }
        if (resource.resourceType === "folder") {
            foldersAndProjectsMap.folders.push(resource);
        }

        if (resource.resourceType === "project") {
            foldersAndProjectsMap.projects.push(resource);
        }
    });
    return foldersAndProjectsMap;
}

/* This function is used to find the scopes for a resource.
 * @param {Object} selectedOrg - selectedOrg data
 * @param {String} scopeId - Id of the resource
 * output - Array of scopes for the resource. Ex: ["gil", "ws1"]  Ex2:  ["gil", "ws2", "test"] }]
 */
export const findPathToScope = (selectedOrg, scopeId, arr = []) => {
    if (!selectedOrg) {
        return [];
    }
    const path = [...arr, selectedOrg.name];
    if (selectedOrg.id === scopeId) {
        return path;
    }
    for (let child of selectedOrg.childScopes) {
        const result = findPathToScope(child, scopeId, path);
        if (result.length) {
            return result;
        }
    }
    return [];
};

export const scopeTypeIconRenderer = (element) => {
    if (element.resourceType === 'project') {
        return <ProjectIcon className={styles["dark-icon"]}/>
    } else if (element.resourceType === "folder") {
        return <FolderIcon className={styles["dark-icon"]}/>
    } else {
        return <OrgIcon className={styles["dark-icon"]}/>
    }
}

export const orgColumnsHeaders = [
    {
        header: "Name",
        accessor: "resourceType",
        id: "resourceType",
        width: 250,
        sort: {
            enabled: true,
        },
        Renderer: ({ row }) => {
            return row.resourceType === "organization" ? (
                <div>
                    <OrgIcon width="20" className={styles["grid-icon"]}></OrgIcon>
                    <span
                        style={{
                            marginLeft: "10px",
                            verticalAlign: "super",
                        }}
                    >
                        {row.name}
                    </span>
                </div>
            ) : row.resourceType === "folder" ? (
                <div>
                    <FolderIcon width="20" className={styles["grid-icon"]}></FolderIcon>
                    <span
                        style={{
                            marginLeft: "10px",
                            verticalAlign: "super",
                        }}
                    >
                        {row.name}
                    </span>
                </div>
            ) : (
                <div>
                    <ProjectIcon width="20" className={styles["grid-icon"]}></ProjectIcon>
                    <span
                        style={{
                            marginLeft: "10px",
                            verticalAlign: "super",
                        }}
                    >
                        {row.name}
                    </span>
                </div>
            );
        },
    },
    {
        header: "Associated Resources",
        accessor: "resources",
        id: "resources",
        width: 200,
        sort: {
            enabled: true,
        },
        Renderer: ({ row }) => {
            return  <div className={styles["orgs-col-alignment"]}><Text>0</Text><Link className={`${styles['orgs-grid-link']}`} to={`./resources?selectedScopeId=${row.id}&selectedScopeName=${row.name}&selectedResourceType=${row.resourceType}`}>View</Link></div>
        },
    },

    {
        header: "User with Access",
        accessor: "access",
        id: "access",
        width: 200,
        sort: {
            enabled: false,
        },
        Renderer: ({ row }) => {
            return  <div className={styles["orgs-col-alignment"]}><Text>0</Text><Link className={`${styles['orgs-grid-link']}`} to={`./users?selectedScopeId=${row.id}&selectedScopeName=${row.name}`}>View</Link></div>
        },
    },
];
export const RESOURCES_SELECTABLE_MAP = {
    Folder: {
        label: "Folder",
        description:
            "Some explanation about the difference between folders and projects. That a folder may have children and grandchildren, but a project is childless.",
        Icon: FolderIcon,
    },
    Project: {
        label: "Project",
        description:
            "Some explanation about the difference between projects and folders. That a folder may have children and grandchildren, but a project is childless.",
        Icon: ProjectIcon,
    }
};

 // Validation for ResourceName and Location
export const validator =  yup.object().shape({
        name: yup.string().required("Field is required.").matches(/^[^    ]*$/, "May not contain space"),
        parent: yup.array().of(yup.object().shape({
            id: yup.string()
                .required("Field is required.")
        }))
})

export const createFilterExpression = (externalfilter, optionString) => {
    if (!externalfilter) {
      externalfilter = `${optionString}`
    } else {
      externalfilter = `${externalfilter} or ${optionString}`
    }
    return externalfilter
}

export const getSelectedResources = (selectedRows) => {
    const selectedResources = [];
    for (const selectedRow of Object.keys(selectedRows)) {
        if (selectedRows[selectedRow]) {
            selectedResources.push(selectedRow);
        }
    }
    return selectedResources;
}

export const recursiveSearch = (recursiveObjectKey, parentData, searchString, isParentMatched = false) => {
    /**
     * recursiveObjectKey: is the key in the object that you want do the recursive search on.
     * parentData: is the main object that you pass to filter out the search.
     * searchString: is the text that you want to search.
     * !!! isParentMatched: No need to pass this parameter, it is being set and passed internally.
     */
    const matchedChildNodes = []
    _.forEach(parentData[recursiveObjectKey], childNode => {
        const childNodeMatched = _.toLower(childNode.name).includes(searchString);
        if (childNode[recursiveObjectKey]?.length > 0) {
            const childNodesResults = recursiveSearch(recursiveObjectKey, childNode, searchString, childNodeMatched);
            if (!isParentMatched) {
                childNode[recursiveObjectKey] = (childNode.resourceType === "folder" && childNodeMatched) ?
                    childNode[recursiveObjectKey] : childNodesResults;
            }
            isParentMatched = false;
        }
        if (childNodeMatched || childNode[recursiveObjectKey]?.length > 0) {
            matchedChildNodes.push(childNode);
        }
    });
    // matchedChildNodes array contains all the objects that match the recursive key-value pair.
    return matchedChildNodes;
}

